import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { generateLearningPath } from "@/lib/learningPaths";

const CHAPTER_OPTIONS = [2, 3, 4, 5, 6];

const SUGGESTED_TOPICS = [
  "Chess openings",
  "How black holes work",
  "Personal finance for beginners",
  "The science of sleep",
  "Introduction to philosophy",
  "Ancient Roman history",
];

export default function StudentLearnNew() {
  const navigate = useNavigate();
  const [topic, setTopic] = useState("");
  const [numChapters, setNumChapters] = useState(3);
  const [selfDescription, setSelfDescription] = useState("");
  const [interestsInput, setInterestsInput] = useState("");
  const [interests, setInterests] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const addInterest = () => {
    const v = interestsInput.trim();
    if (!v) return;
    if (interests.includes(v)) {
      setInterestsInput("");
      return;
    }
    if (interests.length >= 20) {
      toast.error("Max 20 interests.");
      return;
    }
    setInterests([...interests, v]);
    setInterestsInput("");
  };

  const submit = async () => {
    if (topic.trim().length < 2) {
      toast.error("Give your topic a proper title (2+ characters).");
      return;
    }
    setSubmitting(true);
    try {
      const res = await generateLearningPath({
        topic: topic.trim(),
        num_chapters: numChapters,
        self_description: selfDescription.trim(),
        interests,
      });
      toast.success("Your path is being generated — this takes ~30-60s.");
      navigate(`/student/learn/${res.course_id}`);
    } catch (e) {
      const code = e?.response?.data?.detail;
      if (code === "topic_not_allowed") {
        toast.error(
          "That topic isn't allowed here. Please choose something age-appropriate."
        );
      } else if (code === "dob_required") {
        toast.error("Please add your date of birth to your profile first.");
      } else {
        toast.error(code || "Failed to start generation");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-stone-50 text-stone-900"
      data-testid="student-learn-new"
    >
      <DashboardHeader subtitle="Student · New learning path" />
      <main className="mx-auto max-w-2xl px-6 py-8 space-y-5">
        <BackButton to="/student/learn" label="My learning paths" testId="back-to-hub" />

        <div className="space-y-1">
          <div className="flex items-center gap-2 text-amber-700">
            <Sparkles className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wide">
              Design a path in seconds
            </span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            What would you like to learn?
          </h1>
          <p className="text-sm text-stone-600">
            We'll write it in your voice — matched to your age, interests and
            experience.
          </p>
        </div>

        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-stone-600">
              Topic
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="topic-input" className="text-xs">
                What do you want to learn?
              </Label>
              <Input
                id="topic-input"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. Chess openings, How the brain remembers…"
                maxLength={200}
                data-testid="topic-input"
              />
              <div className="flex flex-wrap gap-1.5 pt-1">
                {SUGGESTED_TOPICS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setTopic(s)}
                    className="text-xs px-2 py-1 rounded-full bg-stone-100 hover:bg-amber-100 text-stone-700 hover:text-amber-900 transition"
                    data-testid={`suggest-${s.replace(/\W+/g, "-").toLowerCase()}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="num-chapters" className="text-xs">
                How many chapters?
              </Label>
              <Select
                value={String(numChapters)}
                onValueChange={(v) => setNumChapters(Number(v))}
              >
                <SelectTrigger
                  id="num-chapters"
                  data-testid="num-chapters-select"
                  className="w-32"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHAPTER_OPTIONS.map((n) => (
                    <SelectItem
                      key={n}
                      value={String(n)}
                      data-testid={`num-chapters-option-${n}`}
                    >
                      {n} chapters
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-stone-500">
                More chapters means more depth — but each chapter is still bite-sized.
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-stone-600">
              Tell us about you (optional — makes the path better)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="self-description" className="text-xs">
                A little about yourself
              </Label>
              <Textarea
                id="self-description"
                value={selfDescription}
                onChange={(e) => setSelfDescription(e.target.value)}
                placeholder="e.g. I love football and gaming. I'm curious about how things work."
                rows={3}
                maxLength={1000}
                data-testid="self-description-input"
              />
              <div className="text-xs text-stone-500">
                We use this to adapt tone + examples. Don't share anything
                sensitive.
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="interests-input" className="text-xs">
                Interests (press Enter to add)
              </Label>
              <div className="flex gap-2">
                <Input
                  id="interests-input"
                  value={interestsInput}
                  onChange={(e) => setInterestsInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addInterest();
                    }
                  }}
                  placeholder="football, anime, coding, cooking…"
                  data-testid="interests-input"
                />
                <Button
                  variant="outline"
                  onClick={addInterest}
                  type="button"
                  data-testid="add-interest-button"
                >
                  Add
                </Button>
              </div>
              {interests.length > 0 && (
                <div
                  className="flex flex-wrap gap-1.5 pt-1"
                  data-testid="interests-list"
                >
                  {interests.map((it) => (
                    <Badge
                      key={it}
                      variant="outline"
                      className="bg-amber-50 text-amber-900 border-amber-200 pr-1"
                    >
                      {it}
                      <button
                        onClick={() =>
                          setInterests(interests.filter((x) => x !== it))
                        }
                        className="ml-1 hover:text-red-600"
                        aria-label={`Remove ${it}`}
                        data-testid={`remove-interest-${it.replace(/\W+/g, "-")}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 pt-1">
          <Button
            variant="outline"
            onClick={() => navigate("/student/learn")}
            data-testid="cancel-new-path-button"
          >
            Cancel
          </Button>
          <Button
            className="bg-amber-600 hover:bg-amber-700 text-white"
            onClick={submit}
            disabled={submitting || topic.trim().length < 2}
            data-testid="generate-path-button"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Starting…
              </>
            ) : (
              <>
                <Sparkles className="mr-1 h-4 w-4" /> Generate my path
              </>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
}
